import { Icons } from '@nxsys/types/icons';
import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type SidebarTabItem = {
  label: string;
  key: string;
  children?: SidebarTabItem[];
};

type AppSidebarTabLayoutProps = {
  tabs: SidebarTabItem[];
  activeTab: string;
  onTabChange: (key: string) => void;
  children: ReactNode;
  contentClassName?: string;
};

const CollapsibleSubMenu = ({
  isExpanded,
  children,
}: {
  isExpanded: boolean;
  children: ReactNode;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [isExpanded, children]);

  return (
    <div
      className="overflow-hidden transition-all duration-200 ease-in-out"
      style={{
        maxHeight: isExpanded ? height : 0,
        opacity: isExpanded ? 1 : 0,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
};

const AppSidebarTabLayout = ({
  tabs,
  activeTab,
  onTabChange,
  children,
  contentClassName,
}: AppSidebarTabLayoutProps) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const getParentGroupKey = useCallback(
    (tabKey: string) => {
      for (const tab of tabs) {
        if (tab.children?.some((child) => child.key === tabKey)) {
          return tab.key;
        }
      }
      return null;
    },
    [tabs],
  );

  useEffect(() => {
    const parentKey = getParentGroupKey(activeTab);
    if (parentKey) {
      setExpandedGroups([parentKey]);
    } else {
      setExpandedGroups([]);
    }
  }, [activeTab, getParentGroupKey]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) =>
      prev.includes(groupKey) ? [] : [groupKey],
    );
  };

  const handleTabClick = (key: string) => {
    setExpandedGroups([]);
    onTabChange(key);
  };

  const handleGroupClick = (tab: SidebarTabItem) => {
    const isExpanded = expandedGroups.includes(tab.key);
    const isChildActive = tab.children?.some(
      (child) => activeTab === child.key,
    );

    toggleGroup(tab.key);

    if (!isExpanded && !isChildActive && tab.children?.[0]) {
      onTabChange(tab.children[0].key);
    }
  };

  return (
    <div className="flex h-full gap-4">
      {/* Left Sidebar Menu */}
      <div className="w-[296px] flex-shrink-0 rounded-[12px] bg-white p-4 overflow-y-auto flex flex-col gap-2">
        {tabs.map((tab) => {
          if (tab.children) {
            const isExpanded = expandedGroups.includes(tab.key);
            const isChildActive = tab.children.some(
              (child) => activeTab === child.key,
            );
            return (
              <div key={tab.key}>
                <div
                  className={`
                    cursor-pointer text-[16px] leading-[1.5] font-medium transition-all duration-200
                    flex items-center justify-between py-[10px] pl-[16px] pr-[8px]
                    ${
                      isChildActive && !isExpanded
                        ? 'rounded-[6px] bg-[#F4F4F4] border-l-[4px] border-l-[#00A27A] text-[#000000]'
                        : isExpanded
                          ? 'rounded-[6px] bg-[#F4F4F4] text-[#000000]'
                          : 'rounded-[8px] text-[#3E454D] hover:bg-[#F9FAFB]'
                    }
                  `}
                  onClick={() => handleGroupClick(tab)}
                >
                  <span>{tab.label}</span>
                  <span className="flex items-center gap-2">
                    <span
                      className={`
                        inline-flex items-center justify-center min-w-[20px] h-[20px] px-[6px] rounded-full text-[12px] font-medium leading-none
                        transition-colors duration-200
                        ${
                          isChildActive
                            ? 'bg-[#000000] text-white'
                            : 'bg-[#EEEEEE] text-[#71717A]'
                        }
                      `}
                    >
                      {tab.children.length}
                    </span>
                    <span
                      className={`
                        inline-flex transition-transform duration-200
                        ${isExpanded ? 'rotate-0' : 'rotate-180'}
                      `}
                    >
                      <Icons.IconAngleUp width={16} height={16} />
                    </span>
                  </span>
                </div>
                <CollapsibleSubMenu isExpanded={isExpanded}>
                  <div className="flex px-[14px]">
                    <div className="relative flex flex-col gap-[4px] border-l border-[#E4E4E7] py-[2px] px-[10px] w-full">
                      {tab.children.map((child) => {
                        const isChildItemActive = activeTab === child.key;
                        return (
                          <div
                            key={child.key}
                            className={`
                              relative cursor-pointer text-[14px] leading-[1.43] transition-colors duration-200
                              rounded-[6px] py-[6px] px-[8px]
                              ${
                                isChildItemActive
                                  ? 'font-medium text-[#00A27A]'
                                  : 'font-normal text-[#3E454D] hover:bg-[#F9FAFB]'
                              }
                            `}
                            onClick={() => onTabChange(child.key)}
                          >
                            {isChildItemActive && (
                              <span className="absolute left-[-11px] top-1/2 -translate-y-1/2 h-[32px] w-[2px] bg-[#00A27A] rounded-full" />
                            )}
                            {child.label}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleSubMenu>
              </div>
            );
          }

          const isActive = activeTab === tab.key;
          return (
            <div
              key={tab.key}
              className={`
                cursor-pointer text-[16px] leading-[1.5] font-medium transition-all duration-200
                ${
                  isActive
                    ? 'rounded-[6px] bg-[#F4F4F4] border-l-[4px] border-l-[#00A27A] py-[10px] px-[16px] text-[#000000]'
                    : 'rounded-[8px] py-[10px] px-4 text-[#3E454D] hover:bg-[#F9FAFB]'
                }
              `}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
            </div>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="flex-1 rounded-[12px] bg-white py-6 overflow-y-auto flex justify-center">
        <div className={`w-full ${contentClassName ?? ''}`}>{children}</div>
      </div>
    </div>
  );
};

export default AppSidebarTabLayout;
